import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fechaLarga } from "@/lib/fechas";
import { aPantalla, aServidor, cadaCuanto, importeTxt, nombresTxt, primerAviso } from "@/lib/recordatorios";
import { parseImporte } from "@/lib/gastos";
import { PasoQue, validarPaso1 } from "./PasoQue";
import { PasoCuando, pideDia, validarPaso2 } from "./PasoCuando";
import { PasoQuien, validarPaso3 } from "./PasoQuien";
import { textoAsunto, textoMensaje } from "./MensajeCorreo";
import { relacionInicial, relacionPayload, useNombreRelacion } from "@/components/asuntos/RecordatorioPuntual";

const TITULOS = ["¿Qué quieres recordar?", "¿Cuándo se repite?", "¿Para quién es este recordatorio?"];
const VALIDAR = [validarPaso1, validarPaso2, validarPaso3];

export const formDesde = (g) => ({
  titulo: g?.titulo || "", modo: g?.modo || "aviso", aviso: g?.con_aviso ?? true, importe: g?.importe ?? "", notas: g?.descripcion || "", verNotas: !!g?.descripcion,
  frecuencia: g?.frecuencia || "", dia: g?.dia ?? "", cada_n: g?.cada_n ?? "", unidad: g?.unidad || "meses",
  fecha_inicio: g?.fecha_inicio || "", fin: g?.fecha_fin ? "fecha" : "nunca", fecha_fin: g?.fecha_fin || "",
  para: g?.para || "yo", personas: g?.personas?.length ? g.personas : [""],
  destinatarios: g?.destinatarios?.length ? g.destinatarios.map((d) => ({ nombre: d.nombre, email: d.email, importe: d.importe ?? "" })) : [{ nombre: "", email: "", importe: "" }],
  importeDistinto: !!g?.destinatarios?.some((d) => d.importe != null && d.importe !== ""),
  mensajeEditado: !!g?.mensaje, mensaje: aPantalla(g?.mensaje || ""), asunto: aPantalla(g?.asunto_email || ""),
  rel: relacionInicial(g),
});

const payload = (f) => ({
  ...relacionPayload(f.rel),
  titulo: f.titulo.trim(), descripcion: f.notas.trim(), modo: f.modo, con_aviso: f.aviso, para: f.para,
  personas: f.para === "yo" ? [] : f.personas.map((p) => p.trim()).filter(Boolean),
  importe: f.importe === "" ? null : Number(f.importe), frecuencia: f.frecuencia,
  cada_n: f.frecuencia === "personalizada" ? Number(f.cada_n) : null, unidad: f.frecuencia === "personalizada" ? f.unidad : null,
  dia: pideDia(f) ? Number(f.dia) : null, fecha_inicio: f.fecha_inicio, fecha_fin: f.fin === "fecha" ? f.fecha_fin : "",
  destinatarios: f.modo === "correo" ? f.destinatarios.map((d) => ({ nombre: d.nombre.trim(), email: d.email.trim(), importe: f.importeDistinto && d.importe !== "" ? Number(d.importe) : null })) : [],
  mensaje: f.modo === "correo" ? aServidor(textoMensaje(f)) : "", asunto_email: f.modo === "correo" ? aServidor(textoAsunto(f)) : "", separados: true,
});

const paraTxt = (f) => (f.para === "yo" ? "Solo para ti" : `Para ${nombresTxt(f.personas.map((p) => p.trim()).filter(Boolean))}`);
const correoTxt = (f) => nombresTxt(f.destinatarios.map((d) => d.nombre.trim()));
const fFinal = (f) => ({ ...f, fecha_fin: f.fin === "fecha" ? f.fecha_fin : "" });
const mayus = (t) => t.charAt(0).toUpperCase() + t.slice(1);

const Resumen = ({ f }) => {
  const nombreRel = useNombreRelacion();
  return (
  <div className="space-y-4" data-testid="rp-confirmacion">
    {f.aviso ? <p className="text-xl text-[#1C2421] leading-relaxed" data-testid="rp-confirmacion-texto">{mayus(cadaCuanto(f))} AL DÍA te recordará «{f.titulo.trim()}».</p>
      : <p className="text-xl text-[#1C2421] leading-relaxed" data-testid="rp-confirmacion-texto">{mayus(cadaCuanto(f))} AL DÍA preparará «{f.titulo.trim()}».</p>}
    {f.modo === "correo" && <p className="text-xl text-[#1C2421] leading-relaxed" data-testid="rp-confirmacion-correo">{f.aviso ? "También preparará" : "Dejará preparado"} un correo para {correoTxt(f)}. Tú decidirás si lo envías.</p>}
    <ul className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-2 text-base text-[#4A5550]">
      <li><strong className="text-[#1C2421]">Primer aviso:</strong> {fechaLarga(primerAviso(fFinal(f)))}</li>
      <li><strong className="text-[#1C2421]">Termina:</strong> {f.fin === "fecha" ? fechaLarga(f.fecha_fin) : "cuando tú lo detengas"}</li>
      {f.importe !== "" && <li><strong className="text-[#1C2421]">Importe:</strong> {importeTxt(parseImporte(f.importe))}</li>}
      <li><strong className="text-[#1C2421]">Es:</strong> {paraTxt(f).toLowerCase()}</li>
      <li data-testid="rp-confirmacion-relacion"><strong className="text-[#1C2421]">Relacionado con:</strong> {nombreRel(f.rel)}</li>
    </ul>
  </div>
  );
};

const Creado = ({ f, editando }) => (
  <div className="space-y-5 text-center pt-6" data-testid="rp-creado">
    <span className="w-24 h-24 mx-auto rounded-full bg-[#E8F0EC] flex items-center justify-center"><Icons.CheckCircle2 size={52} className="text-[#3A6B56]" /></span>
    <h2 className="font-heading text-3xl font-bold text-[#1C2421]">{editando ? "Cambios guardados" : "Recordatorio creado"}</h2>
    <ul className="text-left bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-2 text-lg text-[#1C2421]">
      <li data-testid="rp-creado-primero"><strong>Primer aviso:</strong> {fechaLarga(primerAviso(fFinal(f)))}</li>
      <li data-testid="rp-creado-para"><strong>Para quién:</strong> {paraTxt(f)}</li>
      <li data-testid="rp-creado-que"><strong>Qué hará AL DÍA:</strong> {[f.aviso && "avisarte", f.modo === "correo" && `preparar un correo para ${correoTxt(f)}`].filter(Boolean).join(" y ")}</li>
    </ul>
  </div>
);

// Asistente de 3 pasos a pantalla completa. g = recordatorio a editar o base para «crear otro parecido».
export const Asistente = ({ g = null, editarId = null, onCerrar, onVer }) => {
  const [f, setF] = useState(() => formDesde(g));
  const [paso, setPaso] = useState(0); // 0-2 pasos, 3 confirmación, 4 creado
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creadoId, setCreadoId] = useState(null);
  const set = (p) => { setF((x) => ({ ...x, ...p })); setError(null); };
  const falta = paso < 3 ? VALIDAR[paso](f) : null;
  const continuar = () => { setIntento(true); if (falta) return setError(falta); setIntento(false); setPaso(paso + 1); };
  const guardar = async () => {
    setBusy(true);
    try {
      if (editarId) { await api.put(`/gestiones/${editarId}`, payload(f)); setCreadoId(editarId); }
      else { const { data } = await api.post("/gestiones", payload(f)); setCreadoId(data.id); }
      window.dispatchEvent(new Event("aldia-gestiones"));
      setPaso(4);
    } catch (err) { setError({ campo: "final", txt: formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar." }); }
    finally { setBusy(false); }
  };
  const btn = "flex-1 min-h-[56px] rounded-2xl font-bold text-lg disabled:opacity-50";
  return (
    <div className="fixed inset-0 z-[80] bg-[#FAF8F5] flex flex-col" data-testid="rp-asistente" role="dialog" aria-modal="true">
      <header className="px-5 pt-4 pb-3 border-b border-[#E5DFD5] bg-white" style={{ paddingTop: "max(env(safe-area-inset-top), 16px)" }}>
        <div className="max-w-xl mx-auto flex items-start gap-3">
          <div className="flex-1">
            {paso < 3 && <p className="text-base font-semibold text-[#3A6B56]" data-testid="rp-paso">Paso {paso + 1} de 3</p>}
            <h2 className="font-heading text-2xl font-bold text-[#1C2421]" data-testid="rp-titulo-paso">{paso < 3 ? TITULOS[paso] : paso === 3 ? "Revisa antes de crear" : ""}</h2>
          </div>
          {paso < 4 && <button data-testid="rp-cerrar" onClick={onCerrar} aria-label="Cerrar sin guardar" className="min-h-[48px] px-3 rounded-xl text-base font-semibold text-[#4A5550] hover:bg-[#F2EDE4]">Cerrar</button>}
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-5 py-5"><div className="max-w-xl mx-auto pb-6">
        {paso === 0 && <PasoQue f={f} set={set} error={error} />}
        {paso === 1 && <PasoCuando f={f} set={set} error={error} />}
        {paso === 2 && <PasoQuien f={f} set={set} error={error} />}
        {paso === 3 && <Resumen f={f} />}
        {paso === 4 && <Creado f={f} editando={!!editarId} />}
        {error?.campo === "final" && <p className="mt-4 text-base font-semibold text-[#B91C1C]" role="alert">{error.txt}</p>}
      </div></main>
      <footer className="border-t border-[#E5DFD5] bg-white px-5 pt-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 14px)" }}>
        <div className="max-w-xl mx-auto">
          {paso < 3 && falta && intento && <p className="text-sm font-semibold text-[#B91C1C] mb-2" data-testid="rp-falta">Falta: {falta.txt}</p>}
          <div className="flex gap-3">
            {paso < 3 && <>
              <button data-testid="rp-volver" disabled={paso === 0} onClick={() => setPaso(paso - 1)} className={`${btn} bg-[#F2EDE4] border border-[#E5DFD5] text-[#1C2421]`}>Volver</button>
              <button data-testid="rp-continuar" onClick={continuar} className={`${btn} flex-[1.5] bg-[#3A6B56] text-white`}>Continuar</button>
            </>}
            {paso === 3 && <>
              <button data-testid="rp-corregir" onClick={() => setPaso(2)} className={`${btn} bg-[#F2EDE4] border border-[#E5DFD5] text-[#1C2421]`}>Volver y corregir</button>
              <button data-testid="rp-crear" disabled={busy} onClick={guardar} className={`${btn} flex-[1.5] bg-[#3A6B56] text-white`}>{busy ? "Guardando…" : editarId ? "Guardar cambios" : "Crear recordatorio"}</button>
            </>}
            {paso === 4 && <>
              <button data-testid="rp-volver-lista" onClick={onCerrar} className={`${btn} bg-[#F2EDE4] border border-[#E5DFD5] text-[#1C2421] text-base`}>Volver a Pagos y recordatorios</button>
              <button data-testid="rp-ver" onClick={() => onVer(creadoId)} className={`${btn} bg-[#3A6B56] text-white`}>Ver recordatorio</button>
            </>}
          </div>
        </div>
      </footer>
    </div>
  );
};
